import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  addDoc,
  updateDoc,
  DocumentData,
  deleteDoc,
  or,
  and,
} from 'firebase/firestore';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { ref, uploadBytes, getDownloadURL, getBlob } from 'firebase/storage';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { db, storage } from '../firebase';
import {
  Questionnaire,
  QuestionnaireStatus,
} from '../types/questionnaire.types';

const convertToQuestionnaire = (doc: DocumentData): Questionnaire => {
  const data = doc.data();
  return {
    id: doc.id,
    title: data.title,
    description: data.description,
    status: data.status,
    regionID: data.regionID,
    fairID: data.fairID,
    questions: data.questions,
    pdfURL: data.pdfURL,
    createdAt: data.createdAt.toDate(),
    updatedAt: data.updatedAt.toDate(),
  };
};

export const questionnaireService = {
  subscribeToQuestionnaires(
    callback: (questionnaires: Questionnaire[]) => void
  ) {
    const questionnairesRef = collection(db, 'questionnaires');
    return onSnapshot(questionnairesRef, (snapshot) => {
      const questionnaires = snapshot.docs.map(convertToQuestionnaire);
      callback(questionnaires);
    });
  },

  subscribeToQuestionnairesByFair(
    callback: (questionnaires: Questionnaire[]) => void,
    regionID: string,
    fairID: string
  ) {
    const questionnairesRef = collection(db, 'questionnaires');
    const q = query(
      questionnairesRef,
      where('regionID', '==', regionID),
      where('fairID', '==', fairID)
    );
    return onSnapshot(q, (snapshot) => {
      const questionnaires = snapshot.docs.map(convertToQuestionnaire);
      callback(questionnaires);
    });
  },

  subscribeToQuestionnairesByRegion(
    callback: (questionnaires: Questionnaire[]) => void,
    regionID: string
  ) {
    const questionnairesRef = collection(db, 'questionnaires');
    const q = query(questionnairesRef, where('regionID', '==', regionID));
    return onSnapshot(q, (snapshot) => {
      const questionnaires = snapshot.docs.map(convertToQuestionnaire);
      callback(questionnaires);
    });
  },

  subscribeToActiveQuestionnairesByFair(
    callback: (questionnaires: Questionnaire[]) => void,
    fairID: string
  ) {
    const questionnairesRef = collection(db, 'questionnaires');
    const q = query(
      questionnairesRef,
      and(
        where('fairID', '==', fairID),
        or(where('status', '==', 'Required'), where('status', '==', 'Optional'))
      )
    );
    return onSnapshot(q, (snapshot) => {
      const questionnaires = snapshot.docs.map(convertToQuestionnaire);
      callback(questionnaires);
    });
  },

  async createQuestionnaire(
    title: string,
    regionID: string,
    fairID: string
  ): Promise<Questionnaire> {
    const timestamp = new Date();
    const questionnaireData = {
      title,
      description: '',
      status: 'Draft' as QuestionnaireStatus,
      regionID,
      fairID,
      questions: [],
      pdfURL: '',
      createdAt: timestamp,
      updatedAt: timestamp,
    };

    const docRef = await addDoc(
      collection(db, 'questionnaires'),
      questionnaireData
    );

    const questionnaire = {
      id: docRef.id,
      ...questionnaireData,
    };

    return questionnaire;
  },

  async updateQuestionnaire(
    questionnaireID: string,
    questionnaire: Partial<Questionnaire>
  ) {
    const docRef = doc(db, 'questionnaires', questionnaireID);
    const questionnaireData = { ...questionnaire };
    delete questionnaireData.id;

    await updateDoc(docRef, {
      ...questionnaireData,
      updatedAt: new Date(),
    });
  },

  async deleteQuestionnaire(questionnaireID: string) {
    try {
      await deleteDoc(doc(db, 'questionnaires', questionnaireID));
    } catch (error) {
      console.error('Error deleting quesitonairre document:', error);
      throw error;
    }
  },

  async uploadQuestionnairePDF(questionnaireID: string, file: File) {
    try {
      const timestamp = new Date();
      const fileName = `${timestamp.getTime()}_${file.name}`;
      const storageRef = ref(
        storage,
        `questionnaires/${questionnaireID}/forms/${fileName}`
      );
      const uploadResult = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(uploadResult.ref);

      return downloadURL;
    } catch (error) {
      console.error('Error uploading pdf:', error);
      throw error;
    }
  },

  async retrievePDF(url: string) {
    try {
      if (url !== '') {
        const fileRef = ref(storage, url);
        return getBlob(fileRef);
      }
    } catch (error) {
      console.error('Error downloading pdf:', error);
      throw error;
    }
  },
};
